import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {FormControl, FormGroup, ɵFormGroupRawValue, ɵTypedOrUntyped} from '@angular/forms';
import {formatDateFromDateToUiShort, formatDateTimeFromDateToUi} from '@npck/libs/commons';
import {CalendarComponent, CalendarHeaderConfig} from '@npck/libs/ui-kit';
import dayjs from 'dayjs';
import {MIN_DATE} from '../../domain/constants';
import {DateTimeValidators} from '../../infra/date-time.validator';

@Component({
    selector: 'af-date-range-picker-field',
    templateUrl: './date-range-picker-field.component.html',
    styleUrl: './date-range-picker-field.component.scss',
    standalone: false,
})
export class DateRangePickerFieldComponent implements OnInit {
    @ViewChild('picker') dateRangePicker: CalendarComponent;
    @Input() form: FormGroup<DateRangeForm>;
    @Input() hasPickerActions = true;
    @Input() hasTime = true;
    @Input() startDate: dayjs.Dayjs;
    @Input() startTime: string;
    @Input() endDate: dayjs.Dayjs;
    @Input() endTime: string;
    @Input() label: string;
    @Input() hasDynamicWidth = true;
    @Input() iconPosition: 'left' | 'right' = 'right';
    @Input() shouldValidateRange: boolean = false;
    @Input() minDate = MIN_DATE;
    @Input() maxDate = new Date();
    @Input() isDisabled = false;
    @Output() openPickerListener = new EventEmitter<void>();
    @Output() closePickerListener = new EventEmitter<void>();
    headerConfig: CalendarHeaderConfig = {isCustomViewSwitchingEnabled: true};

    ngOnInit(): void {
        this.form.addControl('minWidth', new FormControl({value: '', disabled: this.isDisabled}));
        this.form.addControl('formattedValue', new FormControl({value: '', disabled: this.isDisabled}));
        if (this.form?.controls?.dateFrom?.value) {
            this.form.addControl('dateFrom', new FormControl({value: this.form.controls.dateFrom.value, disabled: this.isDisabled}));
        } else {
            this.form.addControl('dateFrom', new FormControl({value: this.startDate?.toDate(), disabled: this.isDisabled}));
        }
        if (this.hasTime) {
            this.form.addControl('timeFrom', new FormControl({value: this.startTime, disabled: this.isDisabled}));
        }
        if (this.form?.controls?.dateTo?.value) {
            this.form.addControl('dateTo', new FormControl({value: this.form.controls.dateTo.value, disabled: this.isDisabled}));
        } else {
            this.form.addControl('dateTo', new FormControl({value: this.endDate?.toDate(), disabled: this.isDisabled}));
        }
        if (this.hasTime) {
            this.form.addControl('timeTo', new FormControl({value: this.endTime, disabled: this.isDisabled}));
        }
        if (this.shouldValidateRange) {
            this.form.addValidators(DateTimeValidators.rangeValid<DateRangeForm>('dateFrom', 'timeFrom', 'dateTo', 'timeTo'));
        }
        this.modifyDate();
    }

    openPicker(): void {
        this.openPickerListener?.emit();
        this.dateRangePicker.open();
    }

    selectDate(): void {
        this.dateRangePicker._applyPendingSelection();
        const startDate = new Date(this.form.controls.dateFrom.value);
        const today = new Date();
        const isToday = today.getFullYear() === startDate.getFullYear()
            && today.getMonth() === startDate.getMonth()
            && today.getDate() === startDate.getDate();
        if (isToday && this.form.controls.dateTo) {
            this.form.controls.dateTo.setValue(dayjs(today).toDate());
        }

        this.modifyDate();
        this.dateRangePicker.close();
    }

    private modifyDate(): void {
        const formValue = this.form.value;
        if (this.hasTime) {
            formValue.timeFrom = formValue.timeFrom ?? null;
            formValue.timeTo = formValue.timeTo ?? null;
        }
        const dateFrom = formValue.dateFrom && dayjs(formValue.dateFrom);
        const dateTo = formValue.dateTo && dayjs(formValue.dateTo) || dateFrom;
        if (this.hasTime && dateFrom && formValue.timeFrom) {
            const [hours, minutes] = formValue.timeFrom.split(':').map(Number);
            dateFrom.hour(hours).minute(minutes);
            formValue.dateFrom = dateFrom.hour(hours).minute(minutes).second(0).millisecond(0).toDate();
        } else if (this.hasTime && dateFrom && !formValue.timeFrom) {
            formValue.dateFrom = dateFrom.hour(0).minute(0).second(0).millisecond(0).toDate();
        }

        if (this.hasTime && dateTo && formValue.timeTo) {
            const [hours, minutes] = formValue.timeTo.split(':').map(Number);
            formValue.dateTo = dateTo.hour(hours).minute(minutes).second(59).millisecond(99).toDate();
        } else if (this.hasTime && dateTo && !formValue.timeTo) {
            formValue.dateTo = dateTo.hour(23).minute(59).second(59).millisecond(99).toDate();
        } else if (this.hasTime && dateFrom && formValue.timeTo) {
            const [hours, minutes] = (formValue.timeTo).split(':').map(Number);
            formValue.dateTo = dateTo.hour(hours).minute(minutes).second(59).millisecond(99).toDate();
        }
        if (formValue.dateFrom && formValue.dateTo) {
            this.form.setValue(formValue);
            this.form.updateValueAndValidity();
            this.formatDisplayedDateAndCalculateMinWidth();
        }
    }

    private formatDisplayedDateAndCalculateMinWidth(): void {
        const value = this.form.value;
        let formattedStartDate: string;
        let formattedEndDate: string;
        if (this.hasTime) {
            formattedStartDate = value.dateFrom ? formatDateTimeFromDateToUi(value.dateFrom) : '';
            formattedEndDate = value.dateTo ? formatDateTimeFromDateToUi(value.dateTo) : '';
        } else {
            formattedStartDate = value.dateFrom ? formatDateFromDateToUiShort(value.dateFrom) : '';
            formattedEndDate = value.dateTo ? formatDateFromDateToUiShort(value.dateTo) : '';
        }
        const formattedDisplayedDate = formattedStartDate && formattedEndDate ? `${formattedStartDate} - ${formattedEndDate}` : '';
        this.form.controls.formattedValue.setValue(formattedDisplayedDate);
        this.form.controls.minWidth.setValue((formattedDisplayedDate.length + 6) + 'ch');
    }
}

export declare type DateRangeFormValue = ɵTypedOrUntyped<DateRangeForm, ɵFormGroupRawValue<DateRangeForm>, any>;

export interface DateRangeForm {
    minWidth?: FormControl<string>;
    formattedValue?: FormControl<string>;
    dateFrom?: FormControl<Date>;
    timeFrom?: FormControl<string>;
    dateTo?: FormControl<Date>;
    timeTo?: FormControl<string>;
}
